import { useState } from "react";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import RobotPageNav from "@/components/RobotPageNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FinanceApplicationForm } from "@/components/FinanceApplicationForm";
import cc1Img from "@/assets/robots/cc1.jpg";

const CC1 = () => {
  const [financeOpen, setFinanceOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-background">
      <SEO 
        title="PUDU CC1 - Intelligent Commercial Cleaning Robot | Autonomous Floor Scrubber"
        description="4-in-1 versatile autonomous cleaning robot with auto charging, water management, and dynamic space perception. Professional commercial cleaning solution."
        keywords="PUDU CC1, cleaning robot, autonomous scrubber, commercial cleaning, floor cleaning robot, smart cleaning, automated cleaning"
      />
      <Navigation />
      
      <main className="container mx-auto px-4 pt-32 pb-16 md:pt-36">
        <RobotPageNav />
        
        {/* Hero Section */}
        <section className="text-center mb-16 animate-fade-in">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-orbitron font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent leading-tight break-words">
            PUDU CC1
          </h1>
          <p className="text-base md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            Intelligent Commercial Cleaning Robot - 4-in-1 Versatile Cleaning Solution
          </p>
          
          <div className="relative max-w-4xl mx-auto mb-8">
            <img 
              src={cc1Img} 
              alt="PUDU CC1 Intelligent Commercial Cleaning Robot" 
              className="w-full rounded-xl shadow-2xl"
            />
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="glow-gold">
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                Request Demo
              </a>
            </Button>
            <Button size="lg" variant="outline" onClick={() => setFinanceOpen(true)}>
              Apply for Financing
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="tel:3369624539">
                Call: (336) 962-4539
              </a>
            </Button>
          </div>
        </section>

        {/* Key Features Cards */}
        <section className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl mb-3">🔄</div>
            <h3 className="font-bold text-lg mb-2">4-in-1 Cleaning</h3>
            <p className="text-sm text-muted-foreground">Versatile multi-mode cleaning capabilities</p>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl mb-3">⚡</div>
            <h3 className="font-bold text-lg mb-2">Auto Charging</h3>
            <p className="text-sm text-muted-foreground">Automatic charging and water management</p>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl mb-3">🎯</div>
            <h3 className="font-bold text-lg mb-2">Smart Navigation</h3>
            <p className="text-sm text-muted-foreground">Dynamic space perception technology</p>
          </Card>
          <Card className="p-6 text-center glass-effect">
            <div className="text-4xl mb-3">🔧</div>
            <h3 className="font-bold text-lg mb-2">Easy Maintenance</h3>
            <p className="text-sm text-muted-foreground">Diversified consumables system</p>
          </Card>
        </section>

        {/* Tabs */}
        <Tabs defaultValue="features" className="mb-16">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="technology">Technology</TabsTrigger>
            <TabsTrigger value="applications">Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="features" className="space-y-8">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Core Features</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-xl mb-3">4-in-1 Versatile Cleaning</h3>
                  <p className="text-muted-foreground mb-3">
                    Comprehensive cleaning solution combining multiple cleaning methods in one intelligent robot.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Sweeping for debris removal</li>
                    <li>✓ Scrubbing for deep cleaning</li>
                    <li>✓ Mopping for polished finish</li>
                    <li>✓ Drying for immediate use</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">Auto Charging & Water Addition</h3>
                  <p className="text-muted-foreground mb-3">
                    Fully automated operation with minimal human intervention required.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Automatic return to charging station</li>
                    <li>✓ Self-managed water refilling</li>
                    <li>✓ Dirty water disposal system</li>
                    <li>✓ 24/7 operational capability</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">Dynamic Space Perception</h3>
                  <p className="text-muted-foreground mb-3">
                    Advanced navigation system for intelligent path planning and obstacle avoidance.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Real-time environment mapping</li>
                    <li>✓ Adaptive route optimization</li>
                    <li>✓ Intelligent obstacle detection</li>
                    <li>✓ Multi-floor capability</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-3">Diversified Consumables</h3>
                  <p className="text-muted-foreground mb-3">
                    Flexible consumable options for different cleaning requirements and surface types.
                  </p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Multiple brush options</li>
                    <li>✓ Various pad types</li>
                    <li>✓ Easy replacement system</li>
                    <li>✓ Cost-effective maintenance</li>
                  </ul>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="technology">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Advanced Technology</h2>
              <div className="space-y-6">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Intelligent Navigation System</h3>
                  <p className="text-muted-foreground">
                    Multi-sensor fusion technology combining LiDAR, cameras, and ultrasonic sensors for precise navigation and comprehensive environment understanding.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Automated Water Management</h3>
                  <p className="text-muted-foreground">
                    Integrated water supply and waste water collection system enables continuous operation without manual refilling or emptying.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Smart Cleaning Optimization</h3>
                  <p className="text-muted-foreground">
                    AI-powered system automatically adjusts cleaning parameters based on floor type, dirt level, and traffic patterns for optimal results.
                  </p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="font-bold text-lg mb-2">Cloud Management Platform</h3>
                  <p className="text-muted-foreground">
                    Remote monitoring and control through cloud-based platform allows for fleet management, scheduling, and performance analytics.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="applications">
            <Card className="p-8 glass-effect">
              <h2 className="text-2xl font-bold mb-6 text-primary">Ideal Applications</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-primary/20 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-3">Commercial Facilities</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Shopping malls and retail stores</li>
                    <li>✓ Office buildings and corporate spaces</li>
                    <li>✓ Hotels and hospitality venues</li>
                    <li>✓ Convention centers and exhibition halls</li>
                    <li>✓ Entertainment venues and theaters</li>
                  </ul>
                </div>
                <div className="border border-primary/20 rounded-lg p-6">
                  <h3 className="font-bold text-xl mb-3">Public Spaces</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>✓ Airports and transportation hubs</li>
                    <li>✓ Educational institutions and campuses</li>
                    <li>✓ Healthcare facilities (non-sterile areas)</li>
                    <li>✓ Government buildings</li>
                    <li>✓ Sports facilities and stadiums</li>
                  </ul>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Benefits Section */}
        <section className="mb-16">
          <Card className="p-8 glass-effect">
            <h2 className="text-2xl font-bold mb-6 text-primary text-center">Why Choose PUDU CC1?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-3">💰</div>
                <h3 className="font-bold mb-2">Cost Efficient</h3>
                <p className="text-sm text-muted-foreground">
                  Reduce labor costs while improving cleaning quality and consistency
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">⏰</div>
                <h3 className="font-bold mb-2">24/7 Operation</h3>
                <p className="text-sm text-muted-foreground">
                  Continuous cleaning capability ensures facilities stay pristine around the clock
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🌱</div>
                <h3 className="font-bold mb-2">Eco-Friendly</h3>
                <p className="text-sm text-muted-foreground">
                  Optimized water and chemical usage reduces environmental impact
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <Card className="p-12 glass-effect border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Ready to Automate Your Cleaning Operations?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Discover how the PUDU CC1 can transform your facility's cleaning with intelligent, autonomous operation.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" className="glow-gold">
                <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                  Schedule Demo
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="tel:3369624539">
                  Call: (336) 962-4539
                </a>
              </Button>
            </div>
          </Card>
        </section>
      </main>

        <Footer />
    </div>
  );
};

export default CC1;
